import * as React from 'react';
import { markGuideSeen } from '../lib/guide-api.js';
import { t, type MessageKey } from '@reos/i18n';
import { PreparingSection } from '../components/PreparingSection.js';
import { V4Header, V4Segmented } from '../components/ux-v4.js';
import { LeadsTab } from '../components/acquisition-radar/LeadsTab.js';
import { TerritoriesTab } from '../components/acquisition-radar/TerritoriesTab.js';
import { LettersTab } from '../components/acquisition-radar/LettersTab.js';
import { SuppressionTab } from '../components/acquisition-radar/SuppressionTab.js';
import { getRadarStatus, type ConnectionStatusDto } from '../lib/acquisition-radar-api.js';
import type { PreparingSectionCopy } from '../lib/texts.js';
import '../styles/akvizice.css';

/**
 * `/akvizice` (krok AKV1) — akvizicni radar v REOS: Tipy · Uzemi · Dopisy · Vylouceni.
 *
 * Radar je samostatna sluzba a zdroj pravdy; REOS je jen okno do nej (proxy
 * `/v1/acquisition-radar/*`). Kdyz radar neni nakonfigurovany nebo
 * neodpovida, sekce je v SEDEM STAVU „Připravujeme" (stejna komponenta jako
 * nehotove sekce, `PreparingSection`) — zadna chyba na obrazovce.
 *
 * Dopisy REOS NIKDY neodesila: konci se sablonou a tiskovou sadou ke stazeni.
 */

type Tab = 'leads' | 'territories' | 'letters' | 'suppression';

const TABS: readonly { readonly value: Tab; readonly label: MessageKey }[] = [
  { value: 'leads', label: 'webAdmin.acquisitionRadar.tabs.leads' },
  { value: 'territories', label: 'webAdmin.acquisitionRadar.tabs.territories' },
  { value: 'letters', label: 'webAdmin.acquisitionRadar.tabs.letters' },
  { value: 'suppression', label: 'webAdmin.acquisitionRadar.tabs.suppression' },
];

const tr = (key: MessageKey): string => t(key, { locale: 'cs' });

export const AKVIZICE_PREPARING_COPY: PreparingSectionCopy = {
  title: tr('webAdmin.acquisitionRadar.title'),
  perex: tr('webAdmin.acquisitionRadar.preparing.perex'),
  bullets: [
    tr('webAdmin.acquisitionRadar.preparing.bulletTerritories'),
    tr('webAdmin.acquisitionRadar.preparing.bulletLeads'),
    tr('webAdmin.acquisitionRadar.preparing.bulletLetters'),
    tr('webAdmin.acquisitionRadar.preparing.bulletSuppression'),
  ],
};

export function AkvizicePage(): React.JSX.Element {
  const [status, setStatus] = React.useState<ConnectionStatusDto['state'] | 'loading'>('loading');
  const [tab, setTab] = React.useState<Tab>('leads');

  React.useEffect(() => {
    let cancelled = false;
    getRadarStatus()
      .then((s) => {
        if (cancelled) return;
        setStatus(s.state);
        /*
         * Krok PRU1 — ROZHODNUTI PRU1-5: bez napojeneho radaru neni co
         * prochazet; krok „Projděte tipy z akvizice" je hotovy tim, ze makler
         * stranku otevrel a videl, co radar umi. S radarem az detail tipu
         * (`LeadsTab.tsx`).
         */
        if (s.state !== 'ready') void markGuideSeen('acquisition');
      })
      .catch(() => {
        // I selhani samotneho dotazu na stav je pro makléře „zatím nejede".
        if (!cancelled) setStatus('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="rh-v4-page rh-akv" data-testid="akvizice-page">
        <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.loading')}</p>
      </div>
    );
  }

  if (status !== 'ready') {
    return (
      <div className="rh-v4-page rh-akv" data-testid="akvizice-page" data-state={status} data-guide="acquisition">
        <PreparingSection copy={AKVIZICE_PREPARING_COPY} />
        {status === 'unavailable' ? (
          <p className="rh-akv-note" data-testid="akvizice-unavailable">
            {tr('webAdmin.acquisitionRadar.preparing.unavailableNote')}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rh-v4-page rh-akv" data-testid="akvizice-page" data-state="ready">
      <V4Header title={tr('webAdmin.acquisitionRadar.title')} lede={tr('webAdmin.acquisitionRadar.lede')} />
      <div className="rh-akv-scroll-x">
        <V4Segmented<Tab>
          value={tab}
          options={TABS.map((x) => ({ value: x.value, label: tr(x.label) }))}
          onChange={setTab}
          label={tr('webAdmin.acquisitionRadar.tabs.label')}
          testId="akvizice-tab"
        />
      </div>
      {tab === 'leads' ? <LeadsTab onGoTerritories={() => setTab('territories')} /> : null}
      {tab === 'territories' ? <TerritoriesTab /> : null}
      {tab === 'letters' ? <LettersTab /> : null}
      {tab === 'suppression' ? <SuppressionTab /> : null}
    </div>
  );
}
